import React, { useState, useEffect } from 'react';
import { getUserPointSummary } from '../apiClient';
import { useJWTAuth } from '../JWTAuthContext';

interface PointSummaryData {
  points: {
    total_points: number;
    available_points: number;
    spent_points: number;
    current_week_points: number;
    weekly_limit: number;
    weekly_limit_percentage: number;
  };
  ai_tutor: {
    minutes_available: number;
    minutes_used: number;
    points_per_minute: number;
  };
  weekly_activity: {
    week_start_date: string;
    quizzes_completed: number;
    points_earned: number;
    points_spent: number;
  };
  achievements: {
    total_quizzes_with_points: number;
    average_quiz_score: number;
    no_skip_bonuses: number;
    hard_level_bonuses: number;
  };
  status: {
    is_active: boolean;
    admin_override_enabled: boolean;
    last_quiz_completion: string;
  };
}

const PointSummaryWidget: React.FC = () => {
  const { user } = useJWTAuth();
  const [pointData, setPointData] = useState<PointSummaryData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPointSummary = async () => {
      if (user && user.id) {
        setIsLoading(true);
        setError(null);
        
        try {
          const response = await getUserPointSummary(user.id);
          if (response && response.status === 'success' && response.result) {
            setPointData(response.result);
          } else {
            setError('Failed to load point summary');
          }
        } catch (error) {
          console.error('Failed to fetch point summary:', error);
          setError('Error loading points data');
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchPointSummary();
  }, [user]);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const getWeeklyProgressColor = (percentage: number) => {
    if (percentage >= 80) return 'danger';
    if (percentage >= 60) return 'warning';
    return 'success';
  };

  if (isLoading) {
    return (
      <div className="card h-100">
        <div className="card-body d-flex align-items-center justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card h-100">
        <div className="card-body">
          <div className="alert alert-warning" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!pointData) {
    return (
      <div className="card h-100">
        <div className="card-body">
          <div className="alert alert-info" role="alert">
            <i className="fas fa-info-circle me-2"></i>
            No point data available. Complete a quiz to start earning points!
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card h-100 border-0 shadow-sm">
      <div className="card-header bg-gradient-primary text-white">
        <h5 className="card-title mb-0">
          <i className="fas fa-coins me-2"></i>
          Point Summary
        </h5>
      </div>
      
      <div className="card-body p-0">
        {/* Main Points Display */}
        <div className="p-3 border-bottom bg-light">
          <div className="row text-center">
            <div className="col-4">
              <div className="h4 text-primary mb-1">{pointData.points.total_points.toLocaleString()}</div>
              <small className="text-muted">Total Points</small>
            </div>
            <div className="col-4">
              <div className="h4 text-success mb-1">{pointData.points.available_points.toLocaleString()}</div>
              <small className="text-muted">Available</small>
            </div>
            <div className="col-4">
              <div className="h4 text-info mb-1">{Math.floor(pointData.ai_tutor.minutes_available)}</div>
              <small className="text-muted">AI Minutes</small>
            </div>
          </div>
        </div>

        {/* Weekly Progress */}
        <div className="p-3 border-bottom">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-bold">Weekly Progress</span>
            <span className="badge bg-secondary">
              {pointData.points.current_week_points} / {pointData.points.weekly_limit}
            </span>
          </div>
          
          <div className="progress mb-2" style={{ height: '8px' }}>
            <div 
              className={`progress-bar bg-${getWeeklyProgressColor(pointData.points.weekly_limit_percentage)}`}
              style={{ width: `${Math.min(pointData.points.weekly_limit_percentage, 100)}%` }}
            ></div>
          </div>
          
          <div className="row text-center">
            <div className="col-4">
              <div className="fw-bold text-primary">{pointData.weekly_activity.quizzes_completed}</div>
              <small className="text-muted">Quizzes</small>
            </div>
            <div className="col-4">
              <div className="fw-bold text-success">+{pointData.weekly_activity.points_earned}</div>
              <small className="text-muted">Earned</small>
            </div>
            <div className="col-4">
              <div className="fw-bold text-warning">-{pointData.weekly_activity.points_spent}</div>
              <small className="text-muted">Spent</small>
            </div>
          </div>
        </div>

        {/* Achievements */}
        <div className="p-3 border-bottom">
          <h6 className="fw-bold mb-2">
            <i className="fas fa-trophy me-2 text-warning"></i>
            Achievements
          </h6>
          <div className="row">
            <div className="col-6">
              <div className="d-flex align-items-center mb-2">
                <span className="badge bg-primary me-2">{pointData.achievements.total_quizzes_with_points}</span>
                <small>Quizzes Completed</small>
              </div>
              <div className="d-flex align-items-center mb-2">
                <span className="badge bg-success me-2">{pointData.achievements.no_skip_bonuses}</span>
                <small>No-Skip Bonuses</small>
              </div>
            </div>
            <div className="col-6">
              <div className="d-flex align-items-center mb-2">
                <span className="badge bg-info me-2">{pointData.achievements.average_quiz_score.toFixed(1)}%</span>
                <small>Avg Score</small>
              </div>
              <div className="d-flex align-items-center mb-2">
                <span className="badge bg-warning me-2">{pointData.achievements.hard_level_bonuses}</span>
                <small>Hard Level Bonuses</small>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        {/* <div className="p-3">
          <div className="row g-2">
            <div className="col-6">
              <button 
                className="btn btn-outline-primary btn-sm w-100"
                onClick={() => {
                  // Navigate to transaction history
                  window.location.hash = '#/points/transactions';
                }}
              >
                <i className="fas fa-history me-1"></i>
                History
              </button>
            </div>
            <div className="col-6">
              <button 
                className="btn btn-outline-success btn-sm w-100"
                onClick={() => {
                  // Navigate to AI tutor
                  window.location.hash = '#/ai-tutor';
                }}
                disabled={pointData.ai_tutor.minutes_available < 1}
              >
                <i className="fas fa-robot me-1"></i>
                AI Tutor
              </button>
            </div>
          </div>
        </div> */}

        {/* Last Activity */}
        <div className="card-footer bg-light text-center">
          <small className="text-muted">
            Last quiz: {formatDate(pointData.status.last_quiz_completion)}
          </small>
        </div>
      </div>
    </div>
  );
};

export default PointSummaryWidget;